/**
 * Mobile bootstrap — runs once when the app loads.
 * Safely no-ops on web.
 */
import { Capacitor } from "@capacitor/core";

export const isMobile = () => Capacitor.isNativePlatform();

export async function initMobile() {
    if (!isMobile()) return;

    try {
        const { StatusBar, Style } = await import("@capacitor/status-bar");
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: "#0B0B0B" });
        await StatusBar.setOverlaysWebView({ overlay: false });
    } catch (e) { /* plugin missing on web */ }

    try {
        const { SplashScreen } = await import("@capacitor/splash-screen");
        // Defer hide slightly so React has rendered first frame
        setTimeout(() => SplashScreen.hide().catch(() => {}), 600);
    } catch {}

    try {
        const { Keyboard } = await import("@capacitor/keyboard");
        Keyboard.setResizeMode?.({ mode: "body" }).catch(() => {});
    } catch {}

    try {
        const { App } = await import("@capacitor/app");
        // Android hardware back button — exit when at root
        App.addListener("backButton", ({ canGoBack }) => {
            if (canGoBack) {
                window.history.back();
            } else {
                App.exitApp();
            }
        });
    } catch {}
}

/** Open external URLs in the in-app browser (Stripe checkout, T&Cs, etc.) */
export async function openInAppBrowser(url) {
    if (!isMobile()) {
        window.open(url, "_blank");
        return;
    }
    try {
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url, presentationStyle: "popover" });
    } catch {
        window.open(url, "_blank");
    }
}

/** Register for push notifications and return the device token. */
export async function registerPush(onToken, onMessage) {
    if (!isMobile()) return null;
    try {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        const perm = await PushNotifications.requestPermissions();
        if (perm.receive !== "granted") return null;
        await PushNotifications.register();
        PushNotifications.addListener("registration", (t) => onToken?.(t.value));
        PushNotifications.addListener("pushNotificationReceived", (n) => onMessage?.(n));
        return true;
    } catch {
        return null;
    }
}
